import type {FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue} from '@reduxjs/toolkit/query';

type MaybePromise<T> = T | PromiseLike<T>;
import {logApiError, logError} from '../utils/loggerHelpers';

export const BACKEND_PAGE_SIZE = 100;
export const MAX_FETCH_ALL_PAGES = 50;

type PageBaseQuery = (arg: FetchArgs) => MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>;

type PagedFetchSuccess<T> = {
    items: T[];
    totalCount: number;
};

type PagedFetchResult<T> = PagedFetchSuccess<T> | {error: FetchBaseQueryError};

const readTotalCount = (meta: FetchBaseQueryMeta | undefined): number | null => {
    const header = meta?.response?.headers.get('x-total-count');
    if (header === null || header === undefined || header === '') {
        return null;
    }
    const parsed = Number(header);
    return Number.isFinite(parsed) ? parsed : null;
};

export const fetchAllPages = async <T>(
    baseQuery: PageBaseQuery,
    buildRequest: (paging: {limit: number; offset: number}) => FetchArgs,
    extractItems: (data: unknown) => T[],
): Promise<PagedFetchResult<T>> => {
    const items: T[] = [];
    let fetchedPages = 0;
    let totalCount = 0;

    for (let page = 0; page < MAX_FETCH_ALL_PAGES; page += 1) {
        const offset = page * BACKEND_PAGE_SIZE;
        const result = await baseQuery(buildRequest({limit: BACKEND_PAGE_SIZE, offset}));

        if (result.error) {
            if (page > 0) {
                logApiError('GET', `fetchAllPages offset=${offset}`, result.error);
            }
            return {error: result.error};
        }

        const pageItems = extractItems(result.data);
        items.push(...pageItems);
        fetchedPages += 1;

        const headerTotal = readTotalCount(result.meta);
        if (page === 0) {
            totalCount = headerTotal ?? pageItems.length;
        }

        if (pageItems.length === 0 || items.length >= totalCount) {
            break;
        }
    }

    if (items.length < totalCount && fetchedPages >= MAX_FETCH_ALL_PAGES) {
        logError('fetchAllPages stopped at page cap', {
            source: 'api',
            event: 'page_cap_reached',
        });
    }

    return {items, totalCount};
};
